import {
  Dispatch,
  SetStateAction,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useNotification, useTranslation } from '@refinedev/core';

import {
  Button,
  Checkbox,
  Flex,
  Group,
  Loader,
  MultiSelect,
  Stack,
  Text,
  useComputedColorScheme,
  useMantineTheme,
  Badge,
} from '@mantine/core';

import { IconFilePlus } from '@tabler/icons-react';

import { generateReportV2 } from 'api/action';
import { useListTariffsByProvider } from 'api/resource';

import { DatePickerWithPresets } from 'components/common/DatePickerWithPresets';

import { TariffProviderReportHeaders } from 'constants/reportHeaderConstants';
import { ReportType } from 'interfaces';

import { useSettingsStore } from 'store/SettingsStore';
import { useCurrentSiteStore } from 'store/SiteStore';
import { useCurrentUserStore } from 'store/UserStore';

import * as Sentry from '@sentry/react';

import { buttonTextColor } from 'utils/buttonUtils';
import { filterTariffOptions } from 'utils/tariffOptionFilter';

interface TariffProviderReportModalContentProps {
  setIsLoading: Dispatch<SetStateAction<boolean>>;
  isLoading: boolean;
  close?: () => void;
}

export const TariffProviderReportModalContent: React.FC<
  TariffProviderReportModalContentProps
> = ({ setIsLoading, isLoading, close }) => {
  const theme = useMantineTheme();
  const colourMode = useComputedColorScheme('dark');
  const { open } = useNotification();
  const { translate } = useTranslation();
  const { id: siteID } = useCurrentSiteStore();
  const {
    id: userID,
    cognitoID,
    providerID: primaryProviderID,
    providers,
  } = useCurrentUserStore();
  const { fiscalYearEnd } = useSettingsStore();

  const [dateRange, setDateRange] = useState<[string, string]>(['', '']);
  const [tariffSelectEnabled, setTariffSelectEnabled] = useState(false);
  const [selectedTariffIDs, setSelectedTariffIDs] = useState<string[]>([]);

  const resolvedProviderID =
    providers?.[0]?.id ?? primaryProviderID ?? undefined;
  const previousProviderID = useRef<string | undefined>(resolvedProviderID);

  const {
    data: providerTariffs = [],
    isLoading: tariffsLoading,
    isFetching: tariffsFetching,
  } = useListTariffsByProvider({
    providerID: resolvedProviderID,
    userID,
    siteID,
  });

  const options = useMemo(
    () =>
      providerTariffs
        .map((tariff) => ({
          value: tariff.id,
          label: `${tariff.prefix}${tariff.code} - ${tariff.description}`,
          code: `${tariff.prefix}${tariff.code}`,
          description: tariff.description,
        }))
        .sort((a, b) =>
          a.label.localeCompare(b.label, undefined, { numeric: true })
        ),
    [providerTariffs]
  );

  const renderTariffOption = ({ option, checked }: any) => (
    <Group gap="sm" wrap="nowrap">
      <Badge
        variant={checked ? 'filled' : 'light'}
        color={theme.primaryColor}
        radius="sm"
        size="sm"
      >
        {option.code ?? option.label}
      </Badge>
      <Stack gap={2}>
        <Text size="sm" fw={600}>
          {option.description || 'No description'}
        </Text>
        <Text size="xs" c={theme.colors[`gray-${colourMode}`][0]} lineClamp={1}>
          Tariff ID: {option.value}
        </Text>
      </Stack>
    </Group>
  );

  useEffect(() => {
    if (!resolvedProviderID && selectedTariffIDs.length) {
      setSelectedTariffIDs([]);
      return;
    }
    if (
      previousProviderID.current &&
      resolvedProviderID &&
      previousProviderID.current !== resolvedProviderID
    ) {
      setSelectedTariffIDs([]);
    }
    previousProviderID.current = resolvedProviderID;
  }, [resolvedProviderID, selectedTariffIDs.length]);

  const handleCreateReport = async () => {
    if (!resolvedProviderID) return;

    try {
      setIsLoading(true);
      const reportPayload = {
        siteID,
        userID,
        cognitoID,
        startDate: dateRange[0],
        endDate: dateRange[1],
        headers: TariffProviderReportHeaders,
        tariffIDs: tariffSelectEnabled ? selectedTariffIDs : [],
      };

      await generateReportV2(ReportType.tariffProviderReport, reportPayload);
      open?.({
        message: translate('pages:reports.generateReport.inProgress'),
        type: 'success',
        key: 'success',
      });
    } catch (error) {
      Sentry.captureException(error);
      open?.({
        message: translate('pages:reports.generateReport.error'),
        type: 'error',
        key: 'error',
      });
    } finally {
      setIsLoading(false);
      close?.();
    }
  };

  const isDateRangeValid = Boolean(
    dateRange[0] && dateRange[1] && dateRange[0] <= dateRange[1]
  );

  return (
    <Stack gap="md">
      <DatePickerWithPresets
        dateRange={dateRange}
        setDateRange={setDateRange}
        fiscalYearEndMonth={fiscalYearEnd}
      />
      <Checkbox
        label="Select specific tariffs"
        checked={tariffSelectEnabled}
        onChange={(event) => {
          const isChecked = event.currentTarget.checked;
          setTariffSelectEnabled(isChecked);
          if (!isChecked) {
            setSelectedTariffIDs([]);
          }
        }}
      />
      <MultiSelect
        label="Tariffs"
        placeholder="Select tariffs (optional)"
        data={options}
        value={selectedTariffIDs}
        searchable
        filter={filterTariffOptions}
        onChange={setSelectedTariffIDs}
        disabled={!resolvedProviderID || !tariffSelectEnabled}
        renderOption={renderTariffOption}
        rightSection={
          tariffsLoading || tariffsFetching ? (
            <Loader size={18} style={{ marginRight: 8 }} />
          ) : null
        }
        radius="lg"
        w="100%"
      />
      <Flex gap="sm" justify="center">
        <Button
          variant="outline"
          onClick={close}
          color="gray"
          c={buttonTextColor(colourMode, isLoading)}
        >
          {translate('buttons.cancel')}
        </Button>
        <Button
          variant="outline"
          onClick={handleCreateReport}
          c={buttonTextColor(colourMode, isLoading)}
          color="green"
          leftSection={<IconFilePlus />}
          disabled={!isDateRangeValid || !resolvedProviderID}
        >
          {translate('buttons.create')}
        </Button>
      </Flex>
    </Stack>
  );
};
